/**
 * CalendarOverview
 *
 * Inspirational first page of the modal carousel.
 * Decorative themed frame image as background, big beautiful per-calendar
 * display font, floating emoji, tagline, and description.
 */

import React, { useState } from 'react';
import { Icon } from 'semantic-ui-react';
import { useEventModalStore } from '../../stores/useEventModalStore';

/**
 * Per-calendar display font mapping.
 * Each calendar gets a font that matches its personality.
 * All fonts are already loaded via Google Fonts in index.html.
 */
const CALENDAR_FONTS = {
  'rich-bitch':           { family: "'Mea Culpa', cursive",             weight: 400, size: '80px', italic: false },
  'tech-bro':             { family: "'Space Grotesk', sans-serif",      weight: 800, size: '56px', italic: false },
  'festival-fiend':       { family: "'Permanent Marker', cursive",      weight: 400, size: '52px', italic: false },
  'sports-superfan':      { family: "'Righteous', sans-serif",          weight: 400, size: '52px', italic: false },
  'wine-mom':             { family: "'Playfair Display', serif",        weight: 700, size: '56px', italic: true },
  'suburban-dad':         { family: "'Abril Fatface', serif",           weight: 400, size: '52px', italic: false },
  'kpop-stan':            { family: "'Rubik', sans-serif",              weight: 500, size: '56px', italic: false },
  'disney-adult':         { family: "'Sacramento', cursive",            weight: 400, size: '72px', italic: false },
  'jewish-holidays':      { family: "'Alef', sans-serif",              weight: 700, size: '52px', italic: false },
  'all-american':         { family: "'Montserrat', sans-serif",         weight: 700, size: '52px', italic: false },
  'rock-metal':           { family: "'Permanent Marker', cursive",      weight: 400, size: '52px', italic: false },
  'potterhead':           { family: "'Merriweather', serif",            weight: 300, size: '48px', italic: false },
  'broadway-baby':        { family: "'Playfair Display', serif",        weight: 700, size: '52px', italic: true },
  'high-season':          { family: "'Sacramento', cursive",            weight: 400, size: '72px', italic: false },
  'pride':                { family: "'Righteous', sans-serif",          weight: 400, size: '56px', italic: false },
  'latino-heritage':      { family: "'Dancing Script', cursive",        weight: 400, size: '60px', italic: false },
  'formula-1':            { family: "'Space Grotesk', sans-serif",      weight: 800, size: '56px', italic: false },
  'wellness-warrior':     { family: "'Caveat', cursive",                weight: 500, size: '64px', italic: false },
  'chronically-online':   { family: "'Space Grotesk', sans-serif",      weight: 300, size: '48px', italic: false },
  'black-excellence':     { family: "'Montserrat', sans-serif",         weight: 700, size: '52px', italic: false },
  'lunar-aapi':           { family: "'Noto Sans', sans-serif",          weight: 700, size: '48px', italic: false },
  'millennial-nostalgia': { family: "'Pacifico', cursive",              weight: 400, size: '48px', italic: false },
  'vegas-regular':        { family: "'Abril Fatface', serif",           weight: 400, size: '52px', italic: false },
  'space-enthusiast':     { family: "'Space Grotesk', sans-serif",      weight: 800, size: '52px', italic: false },
  'country-music':        { family: "'Amatic SC', cursive",             weight: 400, size: '72px', italic: false },
  'shopping-seasons':     { family: "'Sacramento', cursive",            weight: 400, size: '64px', italic: false },
  'renaissance-faires':   { family: "'Merriweather', serif",            weight: 300, size: '44px', italic: false },
  'fiber-arts':           { family: "'Caveat', cursive",                weight: 500, size: '60px', italic: false },
};

const DEFAULT_FONT = { family: "'Playfair Display', serif", weight: 700, size: '52px', italic: true };

const CalendarOverview = ({ calendar }) => {
  const allEvents = useEventModalStore((state) => state.allEvents);
  const [frameLoaded, setFrameLoaded] = useState(false);

  const font = CALENDAR_FONTS[calendar.id] || DEFAULT_FONT;

  const titleStyle = {
    fontFamily: font.family,
    fontWeight: font.weight,
    fontSize: font.size,
    fontStyle: font.italic ? 'italic' : 'normal',
  };

  // Frame image path - each calendar has a decorative border illustration
  const frameSrc = `/images/calendars/${calendar.id}/overview-frame.webp`;

  return (
    <div className="calendar-overview">
      {/* Decorative frame image - themed border illustration */}
      <img
        className={`overview-frame ${frameLoaded ? 'loaded' : ''}`}
        src={frameSrc}
        alt=""
        loading="eager"
        onLoad={() => setFrameLoaded(true)}
        onError={(e) => { e.target.style.display = 'none'; }}
      />

      {/* Content centered over the frame */}
      <div className="overview-content">
        {/* Floating emoji */}
        <div className="overview-emoji">{calendar.emoji}</div>

        {/* Calendar name - BIG, beautiful, per-calendar font */}
        <h1 className="overview-title" style={titleStyle}>
          {calendar.name}
        </h1>

        {/* Tagline */}
        <p className="overview-tagline">{calendar.tagline}</p>

        {/* Description */}
        <p className="overview-description">{calendar.description}</p>

        {/* CTA hint */}
        <div className="overview-cta">
          <span className="overview-swipe-hint">
            <Icon name="arrow right" /> Swipe to explore {allEvents.length} events
          </span>
        </div>
      </div>
    </div>
  );
};

export default CalendarOverview;
